import { ReceiveShipmentsService } from '@/api';
import { ProcessesService, ProcessStatus } from '@/api/process';
import { GetDeliveriesRecapRow, StoreService } from '@/api/receive';
import { RootState } from '@/app/rootReducer';

import { AppRoutes } from '@/app/routers';
import { useAppDispatch } from '@/app/store';
import { CTAContainer } from '@/components/layout/CTAContainer';
import { ModalAttention } from '@/components/layout/ModalAttention';
import { ModalDataSavedError } from '@/components/layout/ModalDataSaved';
import { UIAction } from '@/components/ui/Action';
import { AlertSnackbar } from '@/components/ui/AlertSnackbar';

import { UIBox } from '@/components/ui/Box';
import { UIButton } from '@/components/ui/Button';
import { UICheckbox } from '@/components/ui/Checkbox';

import { ErrorSnackbar } from '@/components/ui/ErrorSnackbar';
import { PageLoader } from '@/components/ui/PageLoader';
import { Typography } from '@/components/ui/Typography';
import {
  initManageDeliveryState as MNDLInitManageDeliveryState,
  setDeliveryStatuses as MNDLSetDeliveryStatuses,
  setManagedDeliveryInUse as MNDLSetManagedDeliveryInUse,
  setSearchedDeliveryCode as MNDLSetSearchedDeliveryCode,
  setSearchedDeliveryCodes as MNDLSetSearchedDeliveryCodes,
  setSearchedStoreCode as MNDLSetSearchedStoreCode,
  setSearchedStoreCodes as MNDLSetSearchedStoreCodes,
  setShouldFetchDeliveries as MNDLSetShouldFetchDeliveries,
} from '@/features/deliveriesManage/deliveriesManageSlice';
import {
  initManageDeliveryState as PDMInitManageDeliveryState,
  setDeliveryStatuses as PDMSetDeliveryStatuses,
  setManagedDeliveryInUse as PDMSetManagedDeliveryInUse,
  setSearchedDeliveryCode as PDMSetSearchedDeliveryCode,
  setSearchedDeliveryCodes as PDMSetSearchedDeliveryCodes,
  setSearchedStoreCode as PDMSetSearchedStoreCode,
  setSearchedStoreCodes as PDMSetSearchedStoreCodes,
  setShouldFetchDeliveries as PDMSetShouldFetchDeliveries,
} from '@/features/deliveriesManage/partialDeliveriesManageSlice';
import { useSelector } from '@/hooks/useSelector';
import { ReceivingStatus } from '@/pages/Receiving/PageReceiving/types';
import { Autocomplete } from '@material-ui/lab';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useAsync, useDebounce } from 'react-use';
import {
  StyledArrowDropDownIcon,
  StyledDateWrapper,
  StyledField,
  StyledSearchBarLight,
  StyledStartAdornament,
  StyledStatus,
  StyledTablesHeader,
  StyledTablesRow,
  StyledTableWrapper,
} from './style';
import type { DeliveryStatus, Filters } from './types';

const _uniqueArray = (array: string[]): string[] => [
  ...new Set([...array].filter(value => value !== '')),
];

const handleDeliveryStatus = (status: DeliveryStatus): JSX.Element => {
  switch (status) {
    case ReceivingStatus.NEW:
    case ReceivingStatus.PARTIAL:
      return (
        <UIBox alignItems="center">
          <StyledStatus color="#417505" />
          <Typography>{status}</Typography>
        </UIBox>
      );

    case ReceivingStatus.IN_USE:
      return (
        <UIBox alignItems="center">
          <StyledStatus color="#F6BB3B" />
          <Typography>{status}</Typography>
        </UIBox>
      );

    default:
      return (
        <UIBox alignItems="center">
          <StyledStatus color="#E20404" />
          <Typography>{status}</Typography>
        </UIBox>
      );
  }
};

type ManageDeliveryFlowType = 'MNDL' | 'PDM';

const manageDeliveryConfigByFlow = {
  MNDL: {
    selectState: (state: RootState) => state.manageDeliveries,
    flowTypeRequest: undefined,
    status: ReceivingStatus.NEW,
    setDeliveryStatuses: MNDLSetDeliveryStatuses,
    setManagedDeliveryInUse: MNDLSetManagedDeliveryInUse,
    setSearchedDeliveryCode: MNDLSetSearchedDeliveryCode,
    setSearchedDeliveryCodes: MNDLSetSearchedDeliveryCodes,
    setSearchedStoreCode: MNDLSetSearchedStoreCode,
    setSearchedStoreCodes: MNDLSetSearchedStoreCodes,
    setShouldFetchDeliveries: MNDLSetShouldFetchDeliveries,
    initManageDeliveryState: MNDLInitManageDeliveryState,
  },
  PDM: {
    selectState: (state: RootState) => state.partialManageDeliveries,
    flowTypeRequest: 'PDM' as const,
    status: ReceivingStatus.PARTIAL,
    setDeliveryStatuses: PDMSetDeliveryStatuses,
    setManagedDeliveryInUse: PDMSetManagedDeliveryInUse,
    setSearchedDeliveryCode: PDMSetSearchedDeliveryCode,
    setSearchedDeliveryCodes: PDMSetSearchedDeliveryCodes,
    setSearchedStoreCode: PDMSetSearchedStoreCode,
    setSearchedStoreCodes: PDMSetSearchedStoreCodes,
    setShouldFetchDeliveries: PDMSetShouldFetchDeliveries,
    initManageDeliveryState: PDMInitManageDeliveryState,
  },
};

type PageManageDeliveryProps = {
  flowType: ManageDeliveryFlowType;
};

const PageManageDelivery: React.FC<PageManageDeliveryProps> = ({
  flowType,
}) => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();

  const manageDeliveryConfig = manageDeliveryConfigByFlow[flowType];

  const {
    searchedStoreCode,
    searchedStoreCodes,
    searchedDeliveryCode,
    searchedDeliveryCodes,
    deliveryManagedInUse,
    deliveryStatuses,
    shouldFetchDeliveries,
  } = useSelector(manageDeliveryConfig.selectState);

  const [storeCodes, setStoreCodes] = useState<string[]>([]);
  const [deliveries, setDeliveries] = useState<GetDeliveriesRecapRow[]>([]);
  const [deliveriesSorted, setDeliveriesSorted] = useState<boolean>(false);
  const [deliveryNumbers, setDeliveryNumbers] = useState<string[]>([]);
  const [storeCode, setStoreCode] = useState<string>();
  const [selectedDeliveries, setSelectedDeliveries] = useState<string[]>([]);

  const [fetchDeliveries, setFetchDeliveries] = useState<boolean>(false);
  const [unlockModalIsVisible, setUnlockModalVisibility] =
    useState<boolean>(false);
  const [backToStoreModalIsVisible, setBackToStoreModalVisibility] =
    useState<boolean>(false);
  const [disableStoreCodeInput, setDisableStoreCodeInput] =
    useState<boolean>(false);
  const [disableDeliveryCodeInput, setDisableDeliveryCodeInput] =
    useState<boolean>(false);
  const [fetchStoreCodesError, setFetchStoreCodesError] =
    useState<boolean>(false);
  const [fetchDeliveriesError, setFetchDeliveriesError] =
    useState<boolean>(false);
  const [downloadReportError, setDownloadReportError] =
    useState<boolean>(false);
  const [unlockDeliveryError, setUnlockDeliveryError] =
    useState<boolean>(false);
  const [backToStoreError, setBackToStoreError] = useState<boolean>(false);
  const [noReportAvailable, setNoReportAvailable] = useState<boolean>(false);

  useEffect(() => {
    if (searchedStoreCode || searchedDeliveryCode) {
      const initTab = async () => {
        await dispatch(manageDeliveryConfig.setShouldFetchDeliveries(true));
      };
      initTab();
    }
  }, [dispatch, flowType, manageDeliveryConfig]);

  useAsync(async () => {
    /**
     * It runs only if the user go back from the delivery details page
     * (with chrome back arrow)
     */
    if (deliveryManagedInUse) {
      await ReceiveShipmentsService.receiveshipChangeDeliveryStatus({
        requestBody: {
          deliveryNumbers: [deliveryManagedInUse],
          status: manageDeliveryConfig.status,
        },
      });

      dispatch(manageDeliveryConfig.setManagedDeliveryInUse(undefined));
      setFetchDeliveries(true);
    }
  }, []);

  useAsync(async () => {
    try {
      const response = await StoreService.storeserviceGetWholeStoreCodes({
        isActive: false,
      });

      if (response && response.storeCodes) {
        setStoreCodes(response.storeCodes);
      }
    } catch {
      setFetchStoreCodesError(true);
    }
  }, []);

  useAsync(async () => {
    /**
     * shouldFetchDeliveries is true only if the user go back (with  back  button)
     * for confirm from the delivery details page
     * */
    if (fetchDeliveries || shouldFetchDeliveries) {
      try {
        if (shouldFetchDeliveries) {
          dispatch(manageDeliveryConfig.setShouldFetchDeliveries(false));
        }

        if (searchedStoreCodes.length > 0 || searchedDeliveryCodes.length > 0) {
          const response =
            await ReceiveShipmentsService.receiveshipGetDeliveriesRecap({
              requestBody: {
                storeCodes: searchedStoreCodes,
                deliveryNumbers: searchedDeliveryCodes,
                deliveryStatuses: deliveryStatuses,
                ...(manageDeliveryConfig.flowTypeRequest
                  ? { flowType: manageDeliveryConfig.flowTypeRequest }
                  : {}),
              },
            });

          if (response.deliveryList) {
            const { deliveryList } = response;

            setFetchDeliveries(false);
            return setDeliveries(deliveryList);
          }
        }
      } catch {
        setFetchDeliveriesError(true);
      }

      setFetchDeliveries(false);
      setDeliveries([]);
    }
  }, [fetchDeliveries || shouldFetchDeliveries]);

  useEffect(() => {
    if (searchedStoreCode !== '') {
      setDisableDeliveryCodeInput(true);
    } else {
      dispatch(manageDeliveryConfig.setSearchedStoreCodes([]));
      setDisableDeliveryCodeInput(false);
    }
  }, [dispatch, searchedStoreCode]);

  useDebounce(
    () => {
      if (searchedDeliveryCode !== '') {
        const uniqueDeliveryCodes = _uniqueArray([searchedDeliveryCode]);

        dispatch(
          manageDeliveryConfig.setSearchedDeliveryCodes(uniqueDeliveryCodes)
        );
        dispatch(manageDeliveryConfig.setSearchedStoreCodes([]));
        setDisableStoreCodeInput(true);
        setFetchDeliveries(true);
      } else {
        setDisableStoreCodeInput(false);
        dispatch(manageDeliveryConfig.setSearchedDeliveryCodes([]));
      }
    },
    1250,
    [searchedDeliveryCode]
  );

  const changeStoreCodeHandler = (value: string | null): void => {
    const storeCode = value || '';

    if (storeCode !== '') {
      const uniqueStoreCodes = _uniqueArray([storeCode]);

      dispatch(manageDeliveryConfig.setSearchedStoreCode(storeCode));
      dispatch(manageDeliveryConfig.setSearchedStoreCodes(uniqueStoreCodes));
      setFetchDeliveries(true);
    } else {
      dispatch(manageDeliveryConfig.setSearchedStoreCode(''));
    }
    setSelectedDeliveries([]);
  };

  const changeDeliveryCodeHandler = (deliveryCode: string): void => {
    dispatch(manageDeliveryConfig.setSearchedDeliveryCode(deliveryCode));
    if (!!deliveryCode && !selectedDeliveries.includes(deliveryCode)) {
      setSelectedDeliveries([]);
    }
  };

  const downloadReport = async (): Promise<void> => {
    try {
      const response =
        await ReceiveShipmentsService.receiveshipDownLoadReport();

      if (!response) {
        return setNoReportAvailable(true);
      }

      const linkSource = `data:application/csv;base64,${response.csvFile}`;
      const downloadLink = document.createElement('a');
      downloadLink.href = linkSource;
      downloadLink.download = response.nameFile;
      downloadLink.click();
    } catch {
      setDownloadReportError(true);
    }
  };

  const checkboxFilterHandler = (status: DeliveryStatus): void => {
    dispatch(manageDeliveryConfig.setDeliveryStatuses(status));
    setFetchDeliveries(true);
  };

  const [onGoingModalIsVisible, setOnGoingModalVisibility] =
    useState<boolean>(false);
  const [processLock, setProcessLock] = useState<ProcessStatus>();

  const checkProcessOnGoing = async (storeCode: string): Promise<boolean> => {
    const bisInProgress =
      (await ProcessesService.readProcessStatus({
        storeCode: storeCode,
        filterByStoreCode: true,
        process: 'BIS',
      })) || [];

    if (
      Array.isArray(bisInProgress) &&
      bisInProgress.length > 0 &&
      bisInProgress[0].isLocked
    ) {
      setProcessLock(bisInProgress[0]);
      setOnGoingModalVisibility(true);
      return true;
    }
    return false;
  };

  const handleClickViewDeliveryDetails = () => {
    detailsClickHandler(
      searchedStoreCode ||
        deliveries.find(del => del.deliveryNumber === selectedDeliveries[0])
          ?.storeCode ||
        ''
    );
  };

  const detailsClickHandler = async (
    storeCode: string,
    deliveryNumber?: string
  ): Promise<void> => {
    const lockedProcess = await checkProcessOnGoing(storeCode!);
    if (!lockedProcess) {
      const deliveryNumbers =
        selectedDeliveries.length > 0 ? selectedDeliveries : [deliveryNumber];

      flowType === 'PDM'
        ? history.push(
            `${AppRoutes.MANAGE_DELIVERY}/${storeCode}/details?${deliveryNumbers
              .map(dn => `deliveryNumber=${dn}`)
              .join('&')}`,
            {
              flowType,
            }
          )
        : history.push(
            `${AppRoutes.MANAGE_DELIVERY}/${storeCode}/${deliveryNumber}`
          );
    }
  };

  // BACK TO STORE ACTIONS -----------------
  const backToStoreDelivery = async (): Promise<void> => {
    try {
      if (deliveryNumbers && storeCode) {
        await Promise.all(
          deliveryNumbers.map(deliveryNumber => {
            return ReceiveShipmentsService.receiveshipBackToStore({
              requestBody: {
                deliveryNumber,
                storeCode,
              },
            });
          })
        );

        setDeliveryNumbers([]);
        setStoreCode(undefined);
        setFetchDeliveries(true);
        setBackToStoreModalVisibility(false);
      }
    } catch {
      setBackToStoreError(true);
    }
  };

  const backToStoreClickHandler = (
    deliveryNumber: string,
    storeCode: string
  ): void => {
    setDeliveryNumbers([deliveryNumber]);
    setStoreCode(storeCode);
    setBackToStoreModalVisibility(true);
  };

  const backToStoreModalCloseHandler = (): void => {
    setDeliveryNumbers([]);
    setStoreCode(undefined);
    setBackToStoreModalVisibility(false);
  };
  // ---------------------------------------
  // ---------------------------------------

  // UNLOCK ACTIONS ------------------------
  const unlockDelivery = async (): Promise<void> => {
    try {
      await ReceiveShipmentsService.receiveshipChangeDeliveryStatus({
        requestBody: {
          status: manageDeliveryConfig.status,
          deliveryNumbers,
        },
      });

      setDeliveryNumbers([]);
      setFetchDeliveries(true);
      setUnlockModalVisibility(false);
      setSelectedDeliveries([]);
    } catch {
      setUnlockDeliveryError(true);
    }
  };

  const unlockClickHandler = (deliveryNumber: string): void => {
    if (selectedDeliveries?.length > 0) {
      if (selectedDeliveries.includes(deliveryNumber)) {
        setDeliveryNumbers(selectedDeliveries);
      } else {
        setDeliveryNumbers([deliveryNumber]);
      }
      setUnlockModalVisibility(true);
    } else {
      setDeliveryNumbers([deliveryNumber]);
    }
    setUnlockModalVisibility(true);
  };

  const unlockModalCloseHandler = (): void => {
    setDeliveryNumbers([]);
    setUnlockModalVisibility(false);
  };
  // ---------------------------------------
  // ---------------------------------------

  const handleDeliveryAction = (
    status: DeliveryStatus,
    storeCode: string,
    deliveryNumber: string
  ): JSX.Element => {
    switch (status) {
      case ReceivingStatus.NEW:
      case ReceivingStatus.PARTIAL:
        return (
          <UIAction
            label={t('details')}
            icon="search"
            onClick={(): Promise<void> =>
              detailsClickHandler(storeCode, deliveryNumber)
            }
            disabled={
              (selectedDeliveries.length > 0 &&
                !selectedDeliveries.includes(deliveryNumber)) ||
              selectedDeliveries.length >= 2
            }
          />
        );

      case ReceivingStatus.IN_USE:
        return (
          <UIAction
            label={t('unlock')}
            icon="unlock"
            onClick={(): void => unlockClickHandler(deliveryNumber)}
          />
        );

      default:
        return (
          <UIAction
            label={t('backToStore')}
            icon="backupRestore"
            onClick={(): void =>
              backToStoreClickHandler(deliveryNumber, storeCode)
            }
          />
        );
    }
  };

  const filters: Filters = [
    {
      flowType: 'PDM',
      label: t(ReceivingStatus.PARTIAL),
      value: ReceivingStatus.PARTIAL,
      onClick: (): void => checkboxFilterHandler(ReceivingStatus.PARTIAL),
    },
    {
      flowType: 'PDM',
      label: t('inUse'),
      value: ReceivingStatus.IN_USE,
      onClick: (): void => checkboxFilterHandler(ReceivingStatus.IN_USE),
    },
    {
      flowType: 'MNDL',
      label: t('New'),
      value: 'New',
      onClick: (): void => checkboxFilterHandler('New'),
    },
    {
      flowType: 'MNDL',
      label: t('inUse'),
      value: 'In Use',
      onClick: (): void => checkboxFilterHandler('In Use'),
    },
    {
      flowType: 'MNDL',
      label: t('stolen'),
      value: 'Stolen',
      onClick: (): void => checkboxFilterHandler('Stolen'),
    },
  ];

  const dateHandler = (date: string): string => {
    return DateTime.fromFormat(date, 'yyyyMMdd').toFormat('yyyy LLLL dd', {
      locale: i18n.language,
    });
  };

  // SELECT DELIVERIES ACTIONS -----------------
  const IN_USE_STATUS: DeliveryStatus = ReceivingStatus.IN_USE;
  const isInUse = (deliveryStatus?: DeliveryStatus): boolean =>
    deliveryStatus === IN_USE_STATUS;

  const getDeliveryKey = (deliveryNumber?: string): string =>
    deliveryNumber ?? '';

  const [selectedDeliveryStatuses, setSelectedDeliveryStatuses] = useState<
    DeliveryStatus[]
  >([]);

  useEffect(() => {
    const selectedDeliveryStatuses = deliveries
      .filter(({ deliveryNumber }) =>
        selectedDeliveries.includes(getDeliveryKey(deliveryNumber))
      )
      .map(({ deliveryStatus }) => deliveryStatus as DeliveryStatus);
    setSelectedDeliveryStatuses(selectedDeliveryStatuses);
  }, [deliveries, selectedDeliveries]);

  const hasSelectedDeliveries = selectedDeliveryStatuses.length > 0;

  const isDeliverySelectionDisabled = (
    deliveryStatus?: DeliveryStatus
  ): boolean => {
    if (!hasSelectedDeliveries) {
      return false;
    }

    return !selectedDeliveryStatuses.includes(deliveryStatus as DeliveryStatus);
  };

  const toggleDeliverySelection = (
    // storeCode?: string,
    deliveryNumber?: string,
    deliveryStatus?: DeliveryStatus
  ): void => {
    const deliveryKey = getDeliveryKey(deliveryNumber);

    if (deliveryKey === '' || isInUse(deliveryStatus)) {
      return;
    }

    if (
      isDeliverySelectionDisabled(deliveryStatus) &&
      !selectedDeliveries.includes(deliveryKey)
    ) {
      return;
    }

    setSelectedDeliveries(prevState =>
      prevState.includes(deliveryKey)
        ? prevState.filter(selectedDelivery => selectedDelivery !== deliveryKey)
        : [...prevState, deliveryKey]
    );
    // setStoreCode(storeCode);
  };

  const selectableDeliveryKeys = deliveries
    .filter(
      ({ deliveryNumber, deliveryStatus }) =>
        getDeliveryKey(deliveryNumber) !== '' &&
        !isInUse(deliveryStatus as DeliveryStatus) &&
        !isDeliverySelectionDisabled(deliveryStatus as DeliveryStatus)
    )
    .map(({ deliveryNumber }) => getDeliveryKey(deliveryNumber));

  const allDeliveriesSelected =
    selectableDeliveryKeys.length > 0 &&
    selectableDeliveryKeys.every(deliveryKey =>
      selectedDeliveries.includes(deliveryKey)
    );

  const toggleAllDeliveriesSelection = (): void => {
    setSelectedDeliveries(prevState => {
      if (allDeliveriesSelected) {
        return prevState.filter(
          selectedDelivery => !selectableDeliveryKeys.includes(selectedDelivery)
        );
      }

      return Array.from(new Set([...prevState, ...selectableDeliveryKeys]));
    });
    // if (searchedStoreCode) setStoreCode(searchedStoreCode);
  };
  // -------------------------------------------

  return (
    <>
      <ModalAttention
        open={unlockModalIsVisible}
        onConfirmClick={unlockDelivery}
        onClose={unlockModalCloseHandler}
        messageMaxWidth="75%"
        message={t('unlockDeliveryMessage')}
      />
      <ModalAttention
        open={backToStoreModalIsVisible}
        onConfirmClick={backToStoreDelivery}
        onClose={backToStoreModalCloseHandler}
        messageMaxWidth="75%"
        message={t('backToStoreDeliveryMessage')}
      />
      <UIBox flexDirection="column" width="100%" padding={3}>
        <UIBox flexDirection="column">
          <Typography>{t('searchStoreCodeOrDeliveryNumber')}</Typography>
          <UIBox mt={2} flexWrap="wrap">
            <Autocomplete
              autoComplete
              key="store-codes"
              value={searchedStoreCode}
              onChange={(_e, code): void => {
                changeStoreCodeHandler(code);
              }}
              disabled={disableStoreCodeInput}
              options={storeCodes}
              getOptionLabel={(option): string => option || ''}
              getOptionSelected={(option, value): boolean => option === value}
              renderInput={(params): JSX.Element => (
                <StyledField
                  {...params}
                  label={t('storeCode')}
                  variant="outlined"
                />
              )}
            />
            <StyledSearchBarLight
              label=""
              value={searchedDeliveryCode}
              placeholder={t('filterByDeliveryNumber')}
              disabled={disableDeliveryCodeInput}
              loading={false}
              hideButton={true}
              onSearch={(): void => {}}
              onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                changeDeliveryCodeHandler(e.target.value)
              }
              InputProps={{
                startAdornment: (
                  <StyledStartAdornament>
                    <Typography color="grey" size="lg" font="heavy">
                      #
                    </Typography>
                  </StyledStartAdornament>
                ),
              }}
            />
            <UIBox ml={8} flexDirection="column">
              <Typography>{t('filterByStatus')}:</Typography>
              <UIBox mt={1} ml={-1.5}>
                {filters
                  .filter(f => f.flowType === flowType)
                  .map(({ label, value, onClick }, index) => (
                    <UIBox alignItems="center" key={`${value}-${index}`}>
                      <UICheckbox
                        checked={deliveryStatuses.includes(
                          value as DeliveryStatus
                        )}
                        onClick={onClick}
                      />
                      <Typography margin="0 16px 0 -4px">{label}</Typography>
                    </UIBox>
                  ))}
              </UIBox>
            </UIBox>
          </UIBox>
          {fetchDeliveries ? (
            <PageLoader />
          ) : (
            <>
              {deliveries.length > 0 ? (
                <StyledTableWrapper>
                  <StyledTablesHeader $isCheckboxAvailable={flowType === 'PDM'}>
                    {flowType === 'PDM' && (
                      <Typography>
                        {deliveries.filter(
                          del => del.deliveryStatus !== ReceivingStatus.IN_USE
                        ).length > 0 ? (
                          <UICheckbox
                            checked={allDeliveriesSelected}
                            onClick={toggleAllDeliveriesSelection}
                          />
                        ) : null}
                      </Typography>
                    )}
                    <Typography font="heavy">{t('storeCode')}</Typography>
                    <Typography font="heavy">{t('delivery')} #</Typography>
                    <StyledDateWrapper
                      onClick={(): void =>
                        setDeliveriesSorted(prevState => !prevState)
                      }
                    >
                      <Typography font="heavy">{t('arrivalDate')}</Typography>
                      <StyledArrowDropDownIcon $rotated={deliveriesSorted} />
                    </StyledDateWrapper>
                    <Typography font="heavy">
                      {t('shipmentQuantity')}
                    </Typography>
                    <Typography font="heavy">
                      {t('expectedQuantity')}
                    </Typography>
                    <Typography font="heavy">{t('status')}</Typography>
                  </StyledTablesHeader>
                  {deliveries
                    .sort((a, b) =>
                      deliveriesSorted
                        ? Number(a.arrivalDate) - Number(b.arrivalDate)
                        : Number(b.arrivalDate) - Number(a.arrivalDate)
                    )
                    .map(
                      (
                        {
                          storeCode,
                          deliveryNumber,
                          arrivalDate,
                          shipmentQuantity,
                          expectedQuantity,
                          deliveryStatus,
                        },
                        index
                      ) => (
                        <StyledTablesRow
                          key={`${deliveryNumber}-${index}`}
                          $isCheckboxAvailable={flowType === 'PDM'}
                        >
                          {flowType === 'PDM' && (
                            <Typography>
                              {deliveryStatus !== ReceivingStatus.IN_USE && (
                                <UICheckbox
                                  checked={selectedDeliveries.includes(
                                    deliveryNumber ?? ''
                                  )}
                                  disabled={isDeliverySelectionDisabled(
                                    deliveryStatus as DeliveryStatus
                                  )}
                                  onClick={(): void =>
                                    toggleDeliverySelection(
                                      // storeCode,
                                      deliveryNumber,
                                      deliveryStatus as DeliveryStatus
                                    )
                                  }
                                />
                              )}
                            </Typography>
                          )}

                          <Typography>{storeCode}</Typography>
                          <Typography>{deliveryNumber}</Typography>
                          {arrivalDate && (
                            <Typography>{dateHandler(arrivalDate)}</Typography>
                          )}
                          <Typography>{shipmentQuantity}</Typography>
                          <Typography>{expectedQuantity}</Typography>
                          {handleDeliveryStatus(
                            deliveryStatus as DeliveryStatus
                          )}
                          {storeCode && deliveryNumber && (
                            <>
                              {handleDeliveryAction(
                                deliveryStatus as DeliveryStatus,
                                storeCode,
                                deliveryNumber
                              )}
                            </>
                          )}
                        </StyledTablesRow>
                      )
                    )}
                </StyledTableWrapper>
              ) : (
                <>
                  {(searchedStoreCode !== '' ||
                    searchedDeliveryCode !== '') && (
                    <UIBox mt={3}>
                      <Typography font="heavy" size="lg" color="grey">
                        {t('deliveriesUnavailableForTheSelectedStore')}
                      </Typography>
                    </UIBox>
                  )}
                </>
              )}
            </>
          )}
        </UIBox>
        {flowType === 'MNDL' && (
          <CTAContainer
            type="TWO_BUTTONS"
            onClick={downloadReport}
            onBackClick={(): void => history.push(AppRoutes.INTRO)}
            disabledMainAction={false}
            mainButtonLabel={t('downloadReport')}
          />
        )}
        {flowType === 'PDM' && (
          <CTAContainer
            type="TWO_BUTTONS"
            onClick={handleClickViewDeliveryDetails}
            onBackClick={(): void => history.push(AppRoutes.INTRO)}
            disabledMainAction={selectedDeliveries.length <= 1}
            mainButtonLabel={t('pdm.viewDeliveryDetails')}
          />
        )}
        <ErrorSnackbar
          open={fetchStoreCodesError}
          setIsOpen={setFetchStoreCodesError}
          errorMessage={t('error.loadingStoreCodes')}
          ClickAwayListenerProps={{
            onClickAway: (): void => setFetchStoreCodesError(false),
          }}
        />
        <ErrorSnackbar
          open={fetchDeliveriesError}
          setIsOpen={setFetchDeliveriesError}
          errorMessage={t('error.loadingDeliveries')}
          ClickAwayListenerProps={{
            onClickAway: (): void => setFetchDeliveriesError(false),
          }}
        />
        <ErrorSnackbar
          open={downloadReportError}
          setIsOpen={setDownloadReportError}
          errorMessage={t('error.downloadReport')}
          ClickAwayListenerProps={{
            onClickAway: (): void => setDownloadReportError(false),
          }}
        />
        <ErrorSnackbar
          open={unlockDeliveryError}
          setIsOpen={setUnlockDeliveryError}
          errorMessage={t('error.unlockDelivery')}
          ClickAwayListenerProps={{
            onClickAway: (): void => setUnlockDeliveryError(false),
          }}
        />
        <ErrorSnackbar
          open={backToStoreError}
          setIsOpen={setBackToStoreError}
          errorMessage={t('error.backToStoreDelivery')}
          ClickAwayListenerProps={{
            onClickAway: (): void => setBackToStoreError(false),
          }}
        />
        <AlertSnackbar
          open={noReportAvailable}
          setIsOpen={setNoReportAvailable}
          message={t('warning.noReportAvailable')}
        />

        <ModalDataSavedError
          iconType="ERROR"
          $minWidth="705px"
          $minHeight="260px"
          title={t('payAttention')}
          message={t('manageDeliveryDisabled', {
            process: t(processLock?.process || ''),
            lockedBy: processLock?.lockedBy || '',
            store: storeCode || '',
          })}
          open={onGoingModalIsVisible}
          onClose={(): void => {
            setOnGoingModalVisibility(false);
          }}
        >
          <UIBox mt={4} alignItems="center" width="50%">
            <UIButton
              outlined
              label={t('quit')}
              onClick={(): void => setOnGoingModalVisibility(false)}
            />
          </UIBox>
        </ModalDataSavedError>
      </UIBox>
    </>
  );
};

export default PageManageDelivery;
